import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Stack,
  Chip,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import { History as HistoryIcon } from '@mui/icons-material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import ResponsiveTable from '../components/ResponsiveTable';
import {
  AuditLogFromCell,
  AuditLogToCell,
  AuditLogClientLinkCell,
  AuditLogOrderLinkCell,
} from '../components/AuditLogTableCells';
import { formatAuditTarget } from '../utils/auditLogDisplay';
import { formatDateTime } from '../utils/dateFormat';
import AutocompleteField from '../components/inputs/AutocompleteField';
import SelectField from '../components/inputs/SelectField';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../constants/roles';
import { hasPermission } from '../utils/permissions';
import { Navigate } from 'react-router-dom';

const ACTION_LABEL_KEYS = {
  order_created: 'auditLogs.actions.orderCreated',
  order_status_change: 'auditLogs.actions.orderStatusChange',
  item_status_change: 'auditLogs.actions.itemStatusChange',
  item_returned_to_inventory: 'auditLogs.actions.itemReturned',
  item_price_updated: 'auditLogs.actions.itemPriceUpdated',
  order_note_updated: 'auditLogs.actions.orderNoteUpdated',
  order_client_changed: 'auditLogs.actions.orderClientChanged',
  item_client_changed: 'auditLogs.actions.itemClientChanged',
  special_request_created: 'auditLogs.actions.specialRequestCreated',
  special_request_status_updated: 'auditLogs.actions.specialRequestStatusUpdated',
  special_request_converted: 'auditLogs.actions.specialRequestConverted',
  diamond_status_change: 'auditLogs.actions.diamondStatusChange',
  lot_available_carat_updated: 'auditLogs.actions.lotCaratUpdated',
  payment_created: 'auditLogs.actions.paymentCreated',
  payment_cancelled: 'auditLogs.actions.paymentCancelled',
  check_deleted: 'auditLogs.actions.checkDeleted',
  check_discount_updated: 'auditLogs.actions.checkDiscountUpdated',
};

function AuditMobileMetaRow({ label, children }) {
  return (
    <Box display="flex" gap={0.75} alignItems="baseline" flexWrap="wrap" mt={0.35}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ flexShrink: 0, minWidth: 64 }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: '1 1 auto', minWidth: 0, typography: 'body2' }}>
        {children}
      </Box>
    </Box>
  );
}

function getAuditStatusLabel(status, t) {
  if (!status) return '';
  const key = `status.${status}`;
  const translated = t(key, { defaultValue: '' });
  if (translated && translated !== key) return translated;
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function getAuditStatusColor(status) {
  switch (status) {
    case 'requested': return 'warning';
    case 'approved': return 'info';
    case 'ready': return 'primary';
    case 'memo': return 'warning';
    case 'paid': return 'success';
    case 'canceled': case 'cancelled': return 'default';
    case 'open': return 'primary';
    case 'available': return 'success';
    case 'reserved': return 'info';
    case 'sold': return 'warning';
    case 'deleted': return 'error';
    default: return 'default';
  }
}

function getAuditActionColor(action) {
  switch (action) {
    case 'order_created': return 'primary';
    case 'diamond_status_change': return 'secondary';
    case 'lot_available_carat_updated': return 'secondary';
    case 'payment_created': return 'success';
    case 'payment_cancelled': return 'error';
    case 'item_returned_to_inventory': return 'success';
    default: return 'info';
  }
}

const AuditLogs = () => {
  const { t } = useTranslation();
  const { userRole, customPermissions, isInternal } = useAuth();

  const canView = isInternal && (
    userRole === ROLES.MANAGER || hasPermission(userRole, 'view_audit_logs', customPermissions)
  );

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [performedByFilter, setPerformedByFilter] = useState('');
  const [staffUsers, setStaffUsers] = useState([]);

  useEffect(() => {
    if (!canView) return;
    (async () => {
      try {
        const res = await axios.get('/api/users', {
          params: { role: `${ROLES.MANAGER},${ROLES.EMPLOYEE}` },
        });
        setStaffUsers((res.data || []).filter((u) => !u.disabled));
      } catch {
        setStaffUsers([]);
      }
    })();
  }, [canView]);

  const fetchLogs = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo }),
        ...(actionFilter && { action: actionFilter }),
        ...(performedByFilter && { performedBy: performedByFilter }),
      };
      const res = await axios.get('/api/audit-logs', { params });
      setLogs(res.data.logs || []);
      setTotal(res.data.pagination?.total || 0);
    } catch {
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canView, page, rowsPerPage, dateFrom, dateTo, actionFilter, performedByFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const actionOptions = useMemo(
    () => Object.entries(ACTION_LABEL_KEYS).map(([value, labelKey]) => ({
      value,
      label: t(labelKey),
    })),
    [t],
  );

  const columns = useMemo(() => [
    {
      id: 'createdAt',
      label: t('common.date'),
      render: (log) => (
        <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
          {log.createdAt ? formatDateTime(log.createdAt) : '—'}
        </Typography>
      ),
    },
    {
      id: 'action',
      label: t('auditLogs.action'),
      render: (log) => (
        <Chip
          label={t(ACTION_LABEL_KEYS[log.action] || log.action)}
          size="small"
          color={getAuditActionColor(log.action)}
          variant="outlined"
        />
      ),
    },
    {
      id: 'performedBy',
      label: t('auditLogs.performedBy'),
      render: (log) => (
        log.performedBy
          ? `${log.performedBy.firstName || ''} ${log.performedBy.lastName || ''}`.trim() || log.performedBy.email
          : '—'
      ),
    },
    {
      id: 'target',
      label: t('auditLogs.target'),
      render: (log) => formatAuditTarget(log),
    },
    {
      id: 'client',
      label: t('orders.client'),
      render: (log) => <AuditLogClientLinkCell log={log} />,
    },
    {
      id: 'order',
      label: t('orders.orderNumber'),
      render: (log) => <AuditLogOrderLinkCell log={log} />,
    },
    {
      id: 'from',
      label: t('auditLogs.from'),
      render: (log) => (
        <AuditLogFromCell log={log} getAuditStatusLabel={(s) => getAuditStatusLabel(s, t)} />
      ),
    },
    {
      id: 'to',
      label: t('auditLogs.to'),
      render: (log) => (
        <AuditLogToCell
          log={log}
          getAuditStatusLabel={(s) => getAuditStatusLabel(s, t)}
          getToChipColor={getAuditStatusColor}
        />
      ),
    },
  ], [t]);

  const renderMobileCard = useCallback((log) => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={0.5}>
        <Chip
          label={t(ACTION_LABEL_KEYS[log.action] || log.action)}
          size="small"
          color={getAuditActionColor(log.action)}
          variant="outlined"
        />
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {log.createdAt ? formatDateTime(log.createdAt) : '—'}
        </Typography>
      </Box>

      <AuditMobileMetaRow label={`${t('auditLogs.target')}:`}>
        <Typography variant="body2" fontWeight={600} component="span">
          {formatAuditTarget(log)}
        </Typography>
      </AuditMobileMetaRow>

      <AuditMobileMetaRow label={`${t('orders.client')}:`}>
        <AuditLogClientLinkCell log={log} />
      </AuditMobileMetaRow>

      <AuditMobileMetaRow label={`${t('orders.orderNumber')}:`}>
        <AuditLogOrderLinkCell log={log} />
      </AuditMobileMetaRow>

      <AuditMobileMetaRow label={`${t('auditLogs.performedBy')}:`}>
        <Typography variant="body2" component="span">
          {log.performedBy
            ? `${log.performedBy.firstName || ''} ${log.performedBy.lastName || ''}`.trim()
              || log.performedBy.email
            : '—'}
        </Typography>
      </AuditMobileMetaRow>

      <Box display="flex" gap={1} alignItems="center" mt={0.75} flexWrap="wrap">
        <AuditLogFromCell log={log} getAuditStatusLabel={(s) => getAuditStatusLabel(s, t)} />
        <Typography variant="caption" color="text.secondary">→</Typography>
        <AuditLogToCell
          log={log}
          getAuditStatusLabel={(s) => getAuditStatusLabel(s, t)}
          getToChipColor={getAuditStatusColor}
        />
      </Box>
    </Box>
  ), [t]);

  if (!canView) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <HistoryIcon color="action" />
          <Typography variant="h5">{t('auditLogs.title')}</Typography>
        </Box>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
            <TextField
              size="small"
              label={t('auditLogs.dateFrom')}
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              size="small"
              label={t('auditLogs.dateTo')}
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <SelectField
              size="small"
              label={t('auditLogs.action')}
              value={actionFilter}
              onChange={(v) => { setActionFilter(v); setPage(0); }}
              options={actionOptions}
              emptyLabel={t('common.all')}
              includeEmptyOption
              sx={{ minWidth: 200 }}
            />
            <AutocompleteField
              size="small"
              label={t('auditLogs.performedBy')}
              options={staffUsers}
              value={performedByFilter}
              onChange={(id) => { setPerformedByFilter(id || ''); setPage(0); }}
              getOptionLabel={(u) => `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}
              getOptionValue={(u) => u._id}
              isOptionEqualToValue={(opt, val) => opt._id === val._id}
              sx={{ minWidth: 220 }}
            />
          </Stack>
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : logs.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" py={6}>
            {t('auditLogs.noLogs')}
          </Typography>
        ) : (
          <>
            <ResponsiveTable
              columns={columns}
              data={logs}
              renderMobileCard={renderMobileCard}
            />
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Box>
    </Layout>
  );
};

export default AuditLogs;
